/**
 * @module app.core
 */

import { apiCall } from 'app/shared/lib/essette-api';
import { autoinject } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { forceNull } from 'app/shared/lib/utilities';
import { I18N } from 'aurelia-i18n';
import { MemberService } from 'app/cd/services/member-service';
import { Toaster } from 'app/shared/lib/toaster';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class SaveEligibilityMemberPlan extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////

    @bindable
    protected memberId: string | null = null;
    public memberPlan: CD.MemberPlan | null = null;
    protected memberEligibility: CD.MemberEligibility | null = null;
    protected memberPlanId: string | null = null;
    protected memberPlanCustomValues: Core.CustomValues | null = null;
    protected lineOfBusiness: string | null = null;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reset();
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    protected clear(): void {
        this.memberEligibility = {};
        this.memberPlanId = null;
        this.memberPlan = null;
    }

    protected buildPlan(): CD.MemberPlan {
        const savePlan: CD.MemberPlan = {
            id: this.memberPlan?.id,
            healthPlan: {
                id: this.memberEligibility?.product?.healthPlan?.id
            },
            lineOfBusiness: {
                id: this.memberEligibility?.product?.lineOfBusiness?.id
            },
            eligibility: {
                rank: this.memberEligibility?.planRank,
                initial: {
                    dateRange: this.memberEligibility?.dateRange,
                    receivedDate: this.memberEligibility?.receivedDate,
                    shareOfCostAmount: this.memberEligibility?.shareOfCostAmount,
                    employer: this.memberEligibility?.employer,
                    groupNumber: this.memberEligibility?.groupNumber,
                    product: {
                        id: this.memberEligibility?.product?.id,
                    }
                }
            },
            member: {
                id: this.memberId
            },
            identifier: {
                primary: this.memberEligibility?.planDisplayIdentifier
            },
            custom: {
                text: this.parseTextContainer(this.memberPlanCustomValues?.text),
                lookup: this.parseLookupContainer(this.memberPlanCustomValues?.lookup),
            }
        };

        return savePlan;
    }

    // Strip extraneous fields from the UDF Text values and ensure that no empty strings are passed to API
    protected parseTextContainer(text: CD.CustomFieldDefinitionTextContainer | undefined): CD.CustomFieldDefinitionTextContainer | undefined {
        if(!text) return text;
        const VALUE_FIELD = 'value';
        let newText: CD.CustomFieldDefinitionTextContainer = {};
        Object.entries(text).forEach((field: [string, any]) => {
            const fieldKey = field[0];
            if(fieldKey.includes(VALUE_FIELD)) {
                newText = {
                    ...newText,
                    [fieldKey]: forceNull(field[1])
                };
            }
        });

        return newText;
    }

    // Strip extraneous fields from the UDF Lookup Values
    protected parseLookupContainer(lookup: CD.CustomFieldDefinitionLookupContainer | undefined): CD.CustomFieldDefinitionLookupContainer | undefined {
        if(!lookup) return lookup;
        const CHOICE_FIELD = 'choice';
        let newLookup: CD.CustomFieldDefinitionLookupContainer = {};
        Object.entries(lookup).forEach((field: [string, any]) => {
            const fieldKey = field[0];
            if(fieldKey.includes(CHOICE_FIELD)) {
                newLookup = {
                    ...newLookup,
                    [fieldKey]: field[1] ? { code: field[1].code } : null
                };
            }
        });

        return newLookup;
    }

    public loadMemberPlan(plan: CD.MemberPlan): void {
        if(plan) {
            this.memberPlan = plan;
            this.memberPlanId = this.memberPlan.id || null;
            if(this.memberPlan.eligibility) {
                this.memberEligibility = {
                    planRank: this.memberPlan.eligibility.rank,
                    planDisplayIdentifier: this.memberPlan.identifier?.primary,
                    product: { lineOfBusiness: this.memberPlan.lineOfBusiness, healthPlan: this.memberPlan.healthPlan },

                };
            }
            this.memberPlanCustomValues = this.memberPlan?.custom || null;
        }
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        const isSuccess = await this.savePlan();
        return isSuccess;
    }

    public reset(): void {
        this.resetMemberPLanUDFs();
        this.clear();
    }

    public resetMemberPLanUDFs() {
        this.memberPlanCustomValues = {
            text: {},
            lookup: {}
        };
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    protected async savePlan(): Promise<boolean> {
        const insertPlan: CD.MemberPlan = this.buildPlan();

        let isSuccessful = false;
        let errorMessage: string = "";

        try {
            this.state.beginSave();
            if(insertPlan) {
                const response = await this.service.saveMemberPlan(insertPlan);
                isSuccessful = response.isSuccessful;
            } else {
                errorMessage = this.i18n.tr('ui:messages.events.saveFailed', { what: this.i18n.tr('cd:member.plan.$long') });
            }
        }
        catch(error) {
            if(error.description) { errorMessage = error.description; }
            throw error;
        }
        finally {
            if(!isSuccessful) {
                const exception = (this.toaster.toast())
                    .title('Error')
                    .class('ess-toast-error')
                    .icon('ui:icons.error')
                    .message(errorMessage)
                exception.show();
            }
            else {
                if(this.memberPlanId) {
                    this.toaster.standard.updated('cd:member.plan.$long', isSuccessful);
                } else {
                    this.toaster.standard.created('cd:member.plan.$long', isSuccessful);
                }
            }
            this.state.endSave();
            return isSuccessful;
        }
    }

}
